import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const publisherSchema = z.object({
  full_name: z.string().min(2, "Le nom complet doit contenir au moins 2 caractères"),
  email: z.string().email("Veuillez entrer une adresse email valide"),
  phone: z.string().min(8, "Le numéro de téléphone doit contenir au moins 8 chiffres").regex(/^[0-9+\s()-]+$/, "Format de téléphone invalide"),
});

type PublisherFormData = z.infer<typeof publisherSchema>;

interface PublisherInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublisherSaved: (publisherId: string, publisherData: PublisherFormData) => void;
}

export const PublisherInfoDialog = ({ open, onOpenChange, onPublisherSaved }: PublisherInfoDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedPublisher, setSavedPublisher] = useState<PublisherFormData | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<PublisherFormData>({
    resolver: zodResolver(publisherSchema),
  });

  useEffect(() => {
    // Load saved publisher info from localStorage
    const saved = localStorage.getItem("publisher_info");
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setSavedPublisher(data);
        setValue("full_name", data.full_name);
        setValue("email", data.email);
        setValue("phone", data.phone);
      } catch (e) {
        console.error("Error loading saved publisher info:", e);
      }
    }
  }, [setValue]);

  const onSubmit = async (data: PublisherFormData) => {
    setIsSubmitting(true);
    try {
      // Save to publishers table
      const { data: publisher, error } = await supabase
        .from("publishers")
        .insert({
          full_name: data.full_name,
          email: data.email,
          phone: data.phone,
        })
        .select()
        .single();

      if (error) throw error;

      // Save to localStorage for future use
      localStorage.setItem("publisher_info", JSON.stringify(data));

      toast.success("Vos informations ont été enregistrées");
      onPublisherSaved(publisher.id, data);
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving publisher info:", error);
      toast.error("Erreur lors de l'enregistrement de vos informations");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseSaved = () => {
    if (savedPublisher) {
      onSubmit(savedPublisher);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Vos coordonnées</DialogTitle>
          <DialogDescription>
            Pour publier une offre, nous avons besoin de vos informations de contact.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Nom complet *</Label>
            <Input
              id="full_name"
              placeholder="Jean Dupont"
              {...register("full_name")}
            />
            {errors.full_name && (
              <p className="text-sm text-destructive">{errors.full_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="jean.dupont@example.com"
              {...register("email")}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone *</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+242 06 123 45 67"
              {...register("phone")}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone.message}</p>
            )}
          </div>

          <div className="flex gap-2">
            {savedPublisher && (
              <Button
                type="button"
                variant="outline"
                onClick={handleUseSaved}
                disabled={isSubmitting}
                className="flex-1"
              >
                Utiliser mes infos précédentes
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? "Enregistrement..." : "Continuer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
